import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, Sparkles, Eye, Code, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import DOMPurify from "dompurify";
import api from "@/lib/api";

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editorMode, setEditorMode] = useState("visual");
  const [form, setForm] = useState({
    title: "", content: "", summary: "", category_id: "", author_name: "", author_image: "",
    featured_image: "", reading_time: "", is_featured: false, is_published: true,
    is_myth_fact: false, myth_text: "", fact_text: "", priority: "", tags: [],
    scheduled_at: "",
  });

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/articles/${id}`).then(r => setForm(r.data)).catch(() => toast.error("Article not found"));
    }
  }, [id, isEdit]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const generateSummary = async () => {
    if (!form.content) { toast.error("Add content first"); return; }
    setGenerating(true);
    try {
      // Strip HTML for summary generation
      const textContent = form.content.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      const res = await api.post("/ai/summary", { content: textContent });
      set("summary", res.data.summary);
      toast.success("AI summary generated!");
    } catch { toast.error("Failed to generate summary"); }
    setGenerating(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) { toast.error("Title and content are required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/articles/${id}`, form);
        toast.success("Article updated!");
      } else {
        await api.post("/articles", form);
        toast.success("Article created!");
      }
      navigate("/admin/articles");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  return (
    <div data-testid="article-editor">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/articles")} data-testid="back-articles">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-serif font-bold text-foreground">{isEdit ? "Edit Article" : "New Article"}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl space-y-6">
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 space-y-5">
          <div>
            <Label className="font-sans text-sm">Title *</Label>
            <Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Article title" className="mt-1" data-testid="article-title" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="font-sans text-sm">Category</Label>
              <Select value={form.category_id} onValueChange={v => set("category_id", v)}>
                <SelectTrigger className="mt-1" data-testid="article-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-sans text-sm">Reading Time</Label>
              <Input value={form.reading_time} onChange={e => set("reading_time", e.target.value)} placeholder="5 min read" className="mt-1" data-testid="article-reading-time" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="font-sans text-sm">Author Name</Label>
              <Input value={form.author_name} onChange={e => set("author_name", e.target.value)} placeholder="Dr. Jane Doe" className="mt-1" data-testid="article-author" />
            </div>
            <div>
              <Label className="font-sans text-sm">Featured Image URL</Label>
              <Input value={form.featured_image} onChange={e => set("featured_image", e.target.value)} placeholder="https://..." className="mt-1" data-testid="article-image" />
            </div>
          </div>

          {/* WYSIWYG Content Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="font-sans text-sm">Content *</Label>
              <Tabs value={editorMode} onValueChange={setEditorMode} className="w-auto">
                <TabsList className="h-8">
                  <TabsTrigger value="visual" className="text-xs h-7 px-3 font-sans" data-testid="editor-visual-tab">
                    <Eye className="h-3 w-3 mr-1" /> Visual
                  </TabsTrigger>
                  <TabsTrigger value="html" className="text-xs h-7 px-3 font-sans" data-testid="editor-html-tab">
                    <Code className="h-3 w-3 mr-1" /> HTML
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {editorMode === "visual" ? (
              <RichTextEditor
                value={form.content}
                onChange={v => set("content", v)}
                placeholder="Start writing your article..."
              />
            ) : (
              <Textarea
                value={form.content}
                onChange={e => set("content", e.target.value)}
                rows={16}
                placeholder="Write or paste HTML content..."
                className="font-mono text-sm"
                data-testid="article-content-html"
              />
            )}

            {/* Live Preview */}
            {editorMode === "html" && form.content && (
              <div className="mt-4 border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/50">
                <p className="text-[10px] font-sans font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">Preview</p>
                <div
                  className="prose prose-sm max-w-none font-sans text-foreground
                    [&_h2]:text-xl [&_h2]:font-serif [&_h2]:font-bold
                    [&_h3]:text-lg [&_h3]:font-serif [&_h3]:font-bold
                    [&_blockquote]:border-l-4 [&_blockquote]:border-[#1E88E5] [&_blockquote]:pl-4 [&_blockquote]:italic"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.content) }}
                />
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="font-sans text-sm">Summary</Label>
              <Button type="button" variant="outline" size="sm" onClick={generateSummary} disabled={generating} className="text-xs font-sans" data-testid="generate-summary">
                <Sparkles className="h-3 w-3 mr-1" /> {generating ? "Generating..." : "AI Generate"}
              </Button>
            </div>
            <Textarea value={form.summary} onChange={e => set("summary", e.target.value)} rows={3} placeholder="Brief summary..." className="mt-1 font-sans" data-testid="article-summary" />
          </div>

          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Switch checked={form.is_published} onCheckedChange={v => set("is_published", v)} data-testid="article-published" />
              <Label className="font-sans text-sm">Published</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_featured} onCheckedChange={v => set("is_featured", v)} data-testid="article-featured" />
              <Label className="font-sans text-sm">Featured</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_myth_fact} onCheckedChange={v => set("is_myth_fact", v)} data-testid="article-myth-fact" />
              <Label className="font-sans text-sm">Myth vs Fact</Label>
            </div>
          </div>

          {form.is_myth_fact && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/50">
              <div>
                <Label className="font-sans text-sm text-red-600">Myth Text</Label>
                <Textarea value={form.myth_text} onChange={e => set("myth_text", e.target.value)} rows={3} className="mt-1" data-testid="article-myth" />
              </div>
              <div>
                <Label className="font-sans text-sm text-green-600">Fact Text</Label>
                <Textarea value={form.fact_text} onChange={e => set("fact_text", e.target.value)} rows={3} className="mt-1" data-testid="article-fact" />
              </div>
            </div>
          )}

          {/* Content Scheduling */}
          <div className="border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-2 mb-3">
              <CalendarClock className="h-4 w-4 text-[#1E88E5]" />
              <Label className="font-sans text-sm font-semibold">Schedule Publication</Label>
            </div>
            <p className="text-xs font-sans text-muted-foreground mb-3">Leave empty to publish immediately, or set a future date/time.</p>
            <Input
              type="datetime-local"
              value={form.scheduled_at ? form.scheduled_at.slice(0, 16) : ""}
              onChange={e => set("scheduled_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
              className="max-w-xs"
              data-testid="article-schedule"
            />
            {form.scheduled_at && (
              <div className="flex items-center gap-2 mt-2">
                <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 text-xs font-sans border-none">
                  <CalendarClock className="h-3 w-3 mr-1" />
                  Scheduled: {new Date(form.scheduled_at).toLocaleString()}
                </Badge>
                <Button type="button" variant="ghost" size="sm" className="text-xs text-red-500" onClick={() => set("scheduled_at", "")} data-testid="clear-schedule">
                  Clear
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/admin/articles")} className="font-sans">Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="save-article">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save Article"}
          </Button>
        </div>
      </form>
    </div>
  );
}
